import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAllAddress, deleteAddress } from "api/AddressApi";
import { getCustomerId } from "utility/HeaderConfig";
import { toast } from "react-hot-toast";

export default function ShippingAddress({
  onClick,
  viewCart,
  total,
  onAddAddress,
}) {
  const history = useNavigate();
  const customerEntity = getCustomerId();

  const [addresses, setAddresses] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null); // address to confirm delete
  const [deleting, setDeleting] = useState(false);

  /* ── Fetch all addresses for this customer ── */
  const fetchAddresses = () => {
    if (!customerEntity) return;
    setLoadingList(true);
    getAllAddress({ customerEntity })
      .then((d) => {
        const list = d.response || [];
        setAddresses(list);
        /* Auto-select first address if none selected yet */
        if (list.length > 0 && !selectedId) {
          setSelectedId(list[0].id);
        }
      })
      .catch(() =>
        toast.error("Failed to load addresses", { position: "top-right" }),
      )
      .finally(() => setLoadingList(false));
  };

  useEffect(() => {
    fetchAddresses();
    // eslint-disable-next-line
  }, []);

  /* ── Go to AddAddress with cart state ── */
  const handleAddAddress = () => {
    if (onAddAddress) {
      onAddAddress(); // use parent helper (carries cart state)
    } else {
      history("/AddAddress", { state: { viewCart, total } });
    }
  };

  /* ── Edit existing address ── */
  const handleEdit = (addr) => {
    if (onAddAddress) {
      onAddAddress(addr); // pre-fills the form
    } else {
      history("/AddAddress", {
        state: { ...addr, addressId: addr.id, viewCart, total },
      });
    }
  };

  /* ── Delete address ── */
  const confirmDelete = () => {
    if (!deleteTarget) return;
    setDeleting(true);
    deleteAddress(deleteTarget.id)
      .then(() => {
        toast.success("Address removed", { position: "top-right" });
        if (selectedId === deleteTarget.id) setSelectedId(null);
        setDeleteTarget(null);
        fetchAddresses();
      })
      .catch(() =>
        toast.error("Failed to delete address", { position: "top-right" }),
      )
      .finally(() => setDeleting(false));
  };

  /* ── Save & Continue ── */
  const handleContinue = () => {
    const addr = addresses.find((a) => a.id === selectedId);
    if (!addr) {
      toast.error("Please select an address to continue", {
        position: "top-right",
        duration: 2500,
      });
      return;
    }
    /* Build the Details object CheckoutPage expects */
    onClick({
      customerName: addr.customerName || "",
      mobileNo: addr.mobileNo || "",
      email: addr.email || "",
      address1: addr.address1 || "",
      address2: addr.address2 || "",
      pincode: addr.pincodeNumber || "",
      landmark: addr.landmark || "",
      districtName: addr.districtName || "",
      stateName: addr.stateName || "",
      countryName: addr.countryName || "",
      customerEntity: addr.customerEntity || customerEntity,
    });
  };
// eslint-disable-next-line
  const selectedAddr = addresses.find((a) => a.id === selectedId);

  return (
    <>
      <div className="sa-wrap">
        {loadingList ? (
          <div className="sa-loading">
            <span className="sa-spinner" />
            <span>Loading your addresses…</span>
          </div>
        ) : (
          <>
            {/* Address list */}
            {addresses.length === 0 ? (
              <div className="sa-empty">
                <p>No saved addresses yet.</p>
              </div>
            ) : (
              <div className="sa-list">
                {addresses.map((addr) => (
                  <div
                    key={addr.id}
                    className={`sa-card${selectedId === addr.id ? " selected" : ""}`}
                    onClick={() => setSelectedId(addr.id)}
                  >
                    {/* Radio */}
                    <div className="sa-radio">
                      <div className="sa-radio-dot" />
                    </div>

                    {/* Content */}
                    <div className="sa-card-body">
                      <p className="sa-card-name">
                        {addr.customerName || "—"}
                        {addr.isDefault && (
                          <span className="sa-default-badge">Default</span>
                        )}
                      </p>
                      <p className="sa-card-phone">{addr.mobileNo || ""}</p>
                      <p className="sa-card-addr">
                        {[
                          addr.address1,
                          addr.address2,
                          addr.landmark,
                          addr.districtName,
                          addr.stateName,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                      {addr.pincodeNumber && (
                        <p className="sa-card-pin">PIN: {addr.pincodeNumber}</p>
                      )}
                    </div>

                    {/* Edit / Delete */}
                    <div
                      className="sa-card-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="sa-action-btn edit"
                        onClick={() => handleEdit(addr)}
                      >
                        Edit
                      </button>
                      <button
                        className="sa-action-btn del"
                        onClick={() => setDeleteTarget(addr)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add new address */}
            <button className="sa-add-btn" onClick={handleAddAddress}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add New Address
            </button>

            {/* Save & Continue */}
            <div className="sa-footer">
              <button
                className="sa-continue-btn"
                disabled={!selectedId}
                onClick={handleContinue}
              >
                {selectedId ? (
                  <>
                    Save & Continue
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </>
                ) : (
                  "Select an address to continue"
                )}
              </button>
              {!selectedId && addresses.length > 0 && (
                <span className="sa-hint">
                  Please select a delivery address above
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="sa-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Remove Address?</h4>
            <p>
              Are you sure you want to remove the address for{" "}
              <strong>{deleteTarget.customerName}</strong>? This cannot be
              undone.
            </p>
            <div className="sa-modal-btns">
              <button
                className="sa-modal-cancel"
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>
              <button
                className="sa-modal-confirm"
                disabled={deleting}
                onClick={confirmDelete}
              >
                {deleting ? "Removing…" : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
